import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIngredientes } from "../context/IngredientesContext";
import IngredientImage from "../common/IngredientImage";
import { useAccessControl } from "../context/useAccessControl";
import {
  getIngredientStock,
  getIngredientStockChanges,
  hydrateIngredientStockFromRemote,
  saveIngredientStock,
} from "../../services/local/ingredientStock";
import {
  DEFAULT_STOCK_UNIT,
  STOCK_UNIT_OPTIONS,
} from "../../services/local/stockUnits";

const normalizeNumberInput = (value) => {
  const normalized = value.replace(",", ".").replace(/[^0-9.]/g, "");
  const parts = normalized.split(".");

  if (parts.length <= 1) {
    return normalized;
  }

  return `${parts[0]}.${parts.slice(1).join("")}`;
};

const parseStockValue = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const formatPrice = (value) => Number(value || 0).toFixed(2);

const cloneStockMap = (stockMap = {}) => JSON.parse(JSON.stringify(stockMap || {}));

const getStockStatus = (record) => {
  const cantidad = parseStockValue(record?.cantidad);

  if (!record || record.cantidad === "" || record.cantidad === undefined) {
    return {
      label: "Sin cargar",
      tone: {
        backgroundColor: "#f8fafc",
        color: "#475569",
        border: "1px solid #cbd5e1",
      },
      rank: 2,
    };
  }

  if (cantidad <= 0) {
    return {
      label: "Sin stock",
      tone: {
        backgroundColor: "#fef2f2",
        color: "#b91c1c",
        border: "1px solid #fecaca",
      },
      rank: 0,
    };
  }

  return {
    label: "Disponible",
    tone: {
      backgroundColor: "#ecfdf5",
      color: "#166534",
      border: "1px solid #86efac",
    },
    rank: 1,
  };
};

function Stock() {
  const { ingredientes, loadingIngredientes, syncError: ingredientError, cargarIngredientes } = useIngredientes();
  const {
    canAccessModule,
    canPerformAction,
    currentBranch,
    currentAccessRoleLabel,
    currentRestaurant,
  } = useAccessControl();
  const [stockMap, setStockMap] = useState({});
  const [savedStockMap, setSavedStockMap] = useState({});
  const [busqueda, setBusqueda] = useState("");
  const [saveMessage, setSaveMessage] = useState("");
  const [loadingStock, setLoadingStock] = useState(false);
  const [savingStock, setSavingStock] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [needsSaveReview, setNeedsSaveReview] = useState(false);
  const operationRef = useRef(null);
  const stockRef = useRef(stockMap);
  const savedStockRef = useRef(savedStockMap);
  stockRef.current = stockMap;
  savedStockRef.current = savedStockMap;
  const canSeeStockModule = canAccessModule("stock");
  const activeBranchScope = currentBranch || currentRestaurant;
  const branchId = activeBranchScope?.id || "";
  const stockFeatureEnabled = activeBranchScope?.stockEnabled !== false;
  // Permisos v1.1: stock solo admite cambios si manage_stock esta habilitado.
  const canManageStock =
    canSeeStockModule && canPerformAction("manage_stock") && stockFeatureEnabled;
  const stockBusy = loadingStock || savingStock;
  const canEditStock = canManageStock && !stockBusy && !loadError && !needsSaveReview;

  const notifyStockPermissionDenied = () => {
    setSaveMessage(
      `${currentAccessRoleLabel} puede revisar el stock, pero no modificarlo en esta sucursal.`
    );
  };

  const notifyStockFeatureDisabled = () => {
    setSaveMessage(
      "El control de stock esta desactivado para esta sucursal. Puedes volver a activarlo desde Mi Restaurante."
    );
  };

  const reloadStock = useCallback(async ({ preserveDraft = false } = {}) => {
    if (operationRef.current) return;
    const request = {};
    operationRef.current = request;
    const scope = { id: branchId };
    const changes = preserveDraft
      ? getIngredientStockChanges(stockRef.current, savedStockRef.current) : {};
    setLoadingStock(true);
    setLoadError("");
    try {
      const loadedStock = await hydrateIngredientStockFromRemote(scope, { throwOnError: true });
      if (operationRef.current !== request) return;
      setStockMap({ ...loadedStock, ...changes });
      setSavedStockMap(cloneStockMap(loadedStock));
      setNeedsSaveReview(false);
      setSaveMessage(preserveDraft ? "Stock actualizado. Revisa los valores antes de guardar cambios pendientes." : "");
    } catch (error) {
      if (operationRef.current !== request) return;
      setLoadError(error.message || "No pudimos consultar el stock. Vuelve a intentar.");
    } finally {
      if (operationRef.current === request) {
        operationRef.current = null;
        setLoadingStock(false);
      }
    }
  }, [branchId]);

  useEffect(() => {
    const cached = getIngredientStock({ id: branchId });
    setStockMap(cached);
    setSavedStockMap(cloneStockMap(cached));
    setSaveMessage("");
    setNeedsSaveReview(false);
    setSavingStock(false);
    void reloadStock();
    return () => { operationRef.current = null; };
  }, [branchId, reloadStock]);

  const hasPendingChanges = useMemo(
    () => Object.keys(getIngredientStockChanges(stockMap, savedStockMap)).length > 0,
    [savedStockMap, stockMap]
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleBeforeUnload = (event) => {
      if (!hasPendingChanges) {
        return;
      }

      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasPendingChanges]);

  const handleStockChange = (ingredientId, field, value) => {
    if (operationRef.current || loadError || needsSaveReview) return;
    if (!stockFeatureEnabled) {
      notifyStockFeatureDisabled();
      return;
    }

    if (!canManageStock) {
      notifyStockPermissionDenied();
      return;
    }

    setSaveMessage("");
    setStockMap((prev) => ({
      ...prev,
        [ingredientId]: {
          unidad: DEFAULT_STOCK_UNIT,
          cantidad: "",
          ...prev[ingredientId],
          [field]: value,
        updatedAt: new Date().toISOString(),
      },
    }));
  };

  const handleSaveStock = async () => {
    if (operationRef.current || loadError || needsSaveReview || !hasPendingChanges) return;
    if (!stockFeatureEnabled) {
      notifyStockFeatureDisabled();
      return;
    }

    if (!canManageStock) {
      notifyStockPermissionDenied();
      return;
    }

    const request = {};
    operationRef.current = request;
    const submittedStock = cloneStockMap(stockMap);
    setSavingStock(true);
    setSaveMessage("");

    try {
      const result = await saveIngredientStock({ id: branchId }, submittedStock, { baseline: savedStockMap });
      if (operationRef.current !== request) return;

      if (result?.ok === false) {
        setSaveMessage(result.error || "No pudimos guardar el stock.");
        setNeedsSaveReview(result.code === "REQUEST_TIMEOUT");
        return;
      }

      const confirmedStock = { ...submittedStock, ...result?.stockMap };
      setStockMap(confirmedStock);
      setSavedStockMap(cloneStockMap(confirmedStock));
      setSaveMessage("Stock guardado.");
    } catch (error) {
      if (operationRef.current !== request) return;
      setNeedsSaveReview(error?.code === "REQUEST_TIMEOUT");
      setSaveMessage(
        error instanceof Error ? error.message : "No pudimos guardar el stock."
      );
    } finally {
      if (operationRef.current === request) {
        operationRef.current = null;
        setSavingStock(false);
      }
    }
  };

  const adjustCantidad = (ingredientId, delta) => {
    if (!stockFeatureEnabled) {
      notifyStockFeatureDisabled();
      return;
    }

    if (!canManageStock) {
      notifyStockPermissionDenied();
      return;
    }

    const currentValue = parseStockValue(stockMap[ingredientId]?.cantidad);
    const nextValue = Math.max(0, currentValue + delta);
    handleStockChange(ingredientId, "cantidad", String(nextValue));
  };

  const ingredientesConStock = useMemo(() => {
    const filtered = ingredientes.filter((ingrediente) =>
      ingrediente.nombre.toLowerCase().includes(busqueda.toLowerCase())
    );

    return [...filtered]
      .map((ingrediente) => {
        const record = stockMap[ingrediente.id] || {
          unidad: DEFAULT_STOCK_UNIT,
          cantidad: "",
        };
        const status = getStockStatus(record);

        return {
          ...ingrediente,
          stock: record,
          status,
        };
      })
      .sort((a, b) => {
        if (a.status.rank !== b.status.rank) {
          return a.status.rank - b.status.rank;
        }

        return a.nombre.localeCompare(b.nombre);
      });
  }, [busqueda, ingredientes, stockMap]);

  const resumen = useMemo(() => {
    const allStatuses = ingredientes.map((ingrediente) =>
      getStockStatus(stockMap[ingrediente.id] || null).label
    );

    return {
      total: ingredientes.length,
      sinStock: allStatuses.filter((status) => status === "Sin stock").length,
      disponibles: allStatuses.filter((status) => status === "Disponible").length,
      cargados: allStatuses.filter((status) => status !== "Sin cargar").length,
    };
  }, [ingredientes, stockMap]);
  const ingredientStockAlerts = useMemo(
    () =>
      ingredientesConStock.filter(
        (ingrediente) => ingrediente.status.label !== "Disponible"
      ),
    [ingredientesConStock]
  );

  return (
    <div style={styles.page}>
      <section style={styles.header}>
        <div>
          <div style={styles.overline}>Stock</div>
          <h1 style={styles.title}>Control de existencias</h1>
          <p style={styles.subtitle}>
            Aqui cada restaurante lleva su stock por ingrediente, separado del alta base.
            Desde aqui sincronizamos la cantidad real de la sucursal y el descuento
            automatico de los pedidos.
          </p>
        </div>

        <div style={styles.headerBadge}>
          {activeBranchScope?.name || "Modo local"} - stock operativo
        </div>
      </section>

      <section style={styles.metrics}>
        <article style={styles.metricCard}>
          <span style={styles.metricLabel}>Ingredientes</span>
          <strong style={styles.metricValue}>{resumen.total}</strong>
        </article>
        <article style={styles.metricCard}>
          <span style={styles.metricLabel}>Stock cargado</span>
          <strong style={styles.metricValue}>{resumen.cargados}</strong>
        </article>
        <article style={{ ...styles.metricCard, ...styles.metricCardOk }}>
          <span style={styles.metricLabel}>Disponibles</span>
          <strong style={styles.metricValue}>{resumen.disponibles}</strong>
        </article>
        <article style={{ ...styles.metricCard, ...styles.metricCardAlert }}>
          <span style={styles.metricLabel}>Sin stock</span>
          <strong style={styles.metricValue}>{resumen.sinStock}</strong>
        </article>
      </section>

      {!canManageStock ? (
        <section style={styles.emptyPanel}>
          Modo consulta: {currentAccessRoleLabel} puede revisar disponibilidad, pero los cambios
          de stock se habilitan solo para perfiles con permiso operativo.
        </section>
      ) : null}

      {!stockFeatureEnabled ? (
        <section style={styles.emptyPanel}>
          El control de stock esta pausado para esta sucursal. Los pedidos seguiran
          vendiendose sin validar ni descontar existencias hasta volver a activarlo desde
          Mi Restaurante.
        </section>
      ) : null}

      {stockFeatureEnabled && ingredientStockAlerts.length > 0 ? (
        <section style={styles.alertPanel}>
          <div style={styles.alertHeader}>
            <div>
              <div style={styles.alertOverline}>Alerta fuerte de stock</div>
              <h2 style={styles.alertTitle}>Hay insumos que frenan ventas</h2>
              <p style={styles.alertText}>
                Cuando un ingrediente queda sin stock o sin carga, los platos relacionados
                pueden bloquearse en Menu QR, pedidos manuales y flujo operativo.
              </p>
            </div>
            <span style={styles.alertBadge}>
              {ingredientStockAlerts.length} ingrediente
              {ingredientStockAlerts.length === 1 ? "" : "s"} a revisar
            </span>
          </div>

          <div style={styles.alertGrid}>
            {ingredientStockAlerts.slice(0, 6).map((ingrediente) => (
              <article key={ingrediente.id} style={styles.alertCard}>
                <strong style={styles.alertIngredientName}>{ingrediente.nombre}</strong>
                <span style={{ ...styles.statusBadge, ...ingrediente.status.tone }}>
                  {ingrediente.status.label}
                </span>
                <span style={styles.alertIngredientMeta}>
                  Actual: {ingrediente.stock.cantidad || "sin cargar"}{" "}
                  {ingrediente.stock.unidad || "un"}
                </span>
              </article>
            ))}
          </div>

          {ingredientStockAlerts.length > 6 ? (
            <div style={styles.alertMore}>
              Hay {ingredientStockAlerts.length - 6} ingredientes mas pendientes de reposicion o
              carga.
            </div>
          ) : null}
        </section>
      ) : null}

      <section style={styles.toolbar}>
        <input
          type="text"
          value={busqueda}
          onChange={(event) => setBusqueda(event.target.value)}
          placeholder="Buscar ingrediente"
          style={styles.searchInput}
        />
        <div style={styles.toolbarActions}>
          <div
            style={{
              ...styles.helperPill,
              ...(hasPendingChanges
                ? styles.helperPillPending
                : saveMessage
                  ? styles.helperPillSaved
                  : {}),
            }}
          >
            {savingStock ? "Guardando stock..." : loadingStock ? "Consultando stock..." :
              saveMessage || (hasPendingChanges ? "Cambios pendientes sin guardar" :
                "El descuento por pedidos ya impacta en el stock real")}
          </div>
          <button
            type="button"
            onClick={handleSaveStock}
            disabled={!canEditStock || !hasPendingChanges}
            style={{
              ...styles.saveButton,
              ...(!canEditStock || !hasPendingChanges
                ? styles.saveButtonDisabled
                : {}),
            }}
          >
            {savingStock ? "Guardando..." : "Guardar stock"}
          </button>
        </div>
      </section>

      {ingredientError && (
        <section style={styles.emptyPanel} role="alert">
          <p>{ingredientError}</p>
          <button type="button" onClick={() => cargarIngredientes()} disabled={loadingIngredientes} style={styles.saveButton}>
            Reintentar ingredientes
          </button>
        </section>
      )}
      {(loadError || needsSaveReview) && (
        <section style={styles.emptyPanel} role="alert">
          <p>{loadError || saveMessage}</p>
          <p>Tus cambios siguen en pantalla. No se repetira el guardado automaticamente.</p>
          <button type="button" onClick={() => reloadStock({ preserveDraft: true })} disabled={stockBusy} style={styles.saveButton}>
            {needsSaveReview ? "Comprobar guardado" : "Reintentar consulta de stock"}
          </button>
        </section>
      )}
      {!ingredientes.length && loadingIngredientes ? (
        <section style={styles.emptyPanel} role="status">Cargando ingredientes...</section>
      ) : !ingredientes.length && ingredientError ? null
      : ingredientes.length === 0 ? (
        <section style={styles.emptyPanel}>
          Primero crea ingredientes en la pantalla de ingredientes y despues podras cargar
          el stock aqui.
        </section>
      ) : ingredientesConStock.length === 0 ? (
        <section style={styles.emptyPanel}>No encontramos ingredientes con esa busqueda.</section>
      ) : (
        <section style={styles.grid}>
          {ingredientesConStock.map((ingrediente) => (
            <article key={ingrediente.id} style={styles.card}>
              <div style={styles.cardHeader}>
                <div style={styles.ingredientInfo}>
                  <IngredientImage ingredient={ingrediente} style={styles.image} />
                  <div>
                    <strong style={styles.ingredientName}>{ingrediente.nombre}</strong>
                    <div style={styles.ingredientMeta}>${formatPrice(ingrediente.precio)}</div>
                  </div>
                </div>

                <span style={{ ...styles.statusBadge, ...ingrediente.status.tone }}>
                  {ingrediente.status.label}
                </span>
              </div>

              <div style={styles.fieldGrid}>
                <label style={styles.field}>
                  <span style={styles.fieldLabel}>Cantidad actual</span>
                  <input
                    type="text"
                    value={ingrediente.stock.cantidad}
                    onChange={(event) =>
                      handleStockChange(
                        ingrediente.id,
                        "cantidad",
                        normalizeNumberInput(event.target.value)
                      )
                    }
                    placeholder="0"
                    style={styles.input}
                    disabled={!canEditStock}
                  />
                </label>

                <label style={styles.field}>
                  <span style={styles.fieldLabel}>Unidad</span>
                  <select
                    value={ingrediente.stock.unidad || "un"}
                    onChange={(event) =>
                      handleStockChange(ingrediente.id, "unidad", event.target.value)
                    }
                    style={styles.select}
                    disabled={!canEditStock}
                  >
                    {STOCK_UNIT_OPTIONS.map((unidad) => (
                      <option key={unidad.value} value={unidad.value}>
                        {unidad.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div style={styles.quickActions}>
                <button
                  type="button"
                  style={styles.adjustButton}
                  onClick={() => adjustCantidad(ingrediente.id, -1)}
                  disabled={!canEditStock}
                >
                  -1
                </button>
                <button
                  type="button"
                  style={styles.adjustButton}
                  onClick={() => adjustCantidad(ingrediente.id, 1)}
                  disabled={!canEditStock}
                >
                  +1
                </button>
                <button
                  type="button"
                  style={styles.adjustButton}
                  onClick={() => adjustCantidad(ingrediente.id, 5)}
                  disabled={!canEditStock}
                >
                  +5
                </button>
              </div>

              <div style={styles.cardFooter}>
                <span style={styles.footerText}>
                  Actual: {ingrediente.stock.cantidad || "sin cargar"}{" "}
                  {ingrediente.stock.unidad || "un"}
                </span>
              </div>
            </article>
          ))}
        </section>
      )}
    </div>
  );
}

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    padding: "8px 6px 20px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "18px",
    flexWrap: "wrap",
  },
  overline: {
    color: "#2563eb",
    fontSize: "13px",
    fontWeight: 700,
    textTransform: "uppercase",
    marginBottom: "8px",
  },
  title: {
    marginTop: 0,
    marginBottom: "10px",
    color: "#0f172a",
    fontSize: "32px",
  },
  subtitle: {
    margin: 0,
    color: "#475569",
    lineHeight: 1.6,
    maxWidth: "820px",
  },
  headerBadge: {
    padding: "10px 14px",
    borderRadius: "999px",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    fontWeight: 700,
    fontSize: "13px",
  },
  metrics: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "14px",
  },
  metricCard: {
    backgroundColor: "#ffffff",
    borderRadius: "14px",
    padding: "18px",
    border: "1px solid #dbe2ea",
    boxShadow: "0 10px 25px rgba(15, 23, 42, 0.04)",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  metricCardWarn: {
    backgroundColor: "#fffaf0",
  },
  metricCardOk: {
    backgroundColor: "#f6fffb",
  },
  metricCardAlert: {
    backgroundColor: "#fff7f7",
  },
  metricLabel: {
    color: "#64748b",
    fontSize: "13px",
    fontWeight: 600,
  },
  metricValue: {
    fontSize: "28px",
    color: "#0f172a",
  },
  alertPanel: {
    background:
      "linear-gradient(135deg, rgba(255, 247, 237, 0.98) 0%, rgba(255, 255, 255, 0.98) 100%)",
    border: "1px solid #fdba74",
    borderRadius: "20px",
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    boxShadow: "0 18px 38px rgba(194, 65, 12, 0.08)",
  },
  alertHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    flexWrap: "wrap",
  },
  alertOverline: {
    color: "#c2410c",
    fontSize: "12px",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: "6px",
  },
  alertTitle: {
    margin: "0 0 8px",
    color: "#7c2d12",
    fontSize: "28px",
  },
  alertText: {
    margin: 0,
    color: "#9a3412",
    lineHeight: 1.6,
    maxWidth: "820px",
  },
  alertBadge: {
    padding: "10px 14px",
    borderRadius: "999px",
    backgroundColor: "#fff7ed",
    border: "1px solid #fdba74",
    color: "#c2410c",
    fontWeight: 800,
    fontSize: "13px",
  },
  alertGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "14px",
  },
  alertCard: {
    backgroundColor: "rgba(255, 255, 255, 0.94)",
    border: "1px solid #fed7aa",
    borderRadius: "16px",
    padding: "14px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  alertIngredientName: {
    color: "#7c2d12",
    fontSize: "16px",
  },
  alertIngredientMeta: {
    color: "#9a3412",
    fontSize: "13px",
    lineHeight: 1.4,
  },
  alertMore: {
    color: "#9a3412",
    fontWeight: 600,
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "14px",
    flexWrap: "wrap",
  },
  toolbarActions: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  searchInput: {
    width: "100%",
    maxWidth: "320px",
    borderRadius: "12px",
    border: "1px solid #dbe2ea",
    padding: "12px 14px",
    fontSize: "15px",
    boxSizing: "border-box",
  },
  helperPill: {
    padding: "8px 12px",
    borderRadius: "999px",
    backgroundColor: "#f8fafc",
    border: "1px solid #cbd5e1",
    color: "#475569",
    fontWeight: 600,
    fontSize: "13px",
  },
  helperPillPending: {
    backgroundColor: "#fff7ed",
    border: "1px solid #fdba74",
    color: "#c2410c",
  },
  helperPillSaved: {
    backgroundColor: "#ecfdf5",
    border: "1px solid #86efac",
    color: "#166534",
  },
  saveButton: {
    border: "none",
    borderRadius: "12px",
    backgroundColor: "#0f172a",
    color: "#ffffff",
    padding: "11px 16px",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: "14px",
  },
  saveButtonDisabled: {
    backgroundColor: "#cbd5e1",
    color: "#64748b",
    cursor: "not-allowed",
  },
  emptyPanel: {
    padding: "24px",
    borderRadius: "16px",
    backgroundColor: "#ffffff",
    border: "1px dashed #cbd5e1",
    color: "#64748b",
    lineHeight: 1.6,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "16px",
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    border: "1px solid #dbe2ea",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.04)",
    padding: "18px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
  },
  ingredientInfo: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    minWidth: 0,
  },
  image: {
    width: "58px",
    height: "58px",
    borderRadius: "14px",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundColor: "#f1f5f9",
    flexShrink: 0,
  },
  ingredientName: {
    color: "#0f172a",
    display: "block",
    marginBottom: "4px",
  },
  ingredientMeta: {
    color: "#64748b",
    fontSize: "13px",
  },
  statusBadge: {
    padding: "8px 10px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  fieldGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "12px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  fieldLabel: {
    color: "#475569",
    fontSize: "13px",
    fontWeight: 700,
  },
  input: {
    width: "100%",
    borderRadius: "12px",
    border: "1px solid #dbe2ea",
    padding: "11px 12px",
    fontSize: "15px",
    boxSizing: "border-box",
  },
  select: {
    width: "100%",
    borderRadius: "12px",
    border: "1px solid #dbe2ea",
    padding: "11px 12px",
    fontSize: "15px",
    boxSizing: "border-box",
    backgroundColor: "#ffffff",
  },
  quickActions: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  },
  adjustButton: {
    border: "1px solid #dbe2ea",
    backgroundColor: "#ffffff",
    color: "#334155",
    borderRadius: "10px",
    padding: "8px 12px",
    fontWeight: 700,
    cursor: "pointer",
    minWidth: "54px",
  },
  cardFooter: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
    color: "#64748b",
    fontSize: "13px",
  },
  footerText: {
    whiteSpace: "nowrap",
  },
};

export default Stock;
